import React from "react";
import ReactDOM from "react-dom";
import { t } from "../utils/i18n";
import {
  MOOD_KEYS, MOOD_WISH_KEYS, MOOD_ICONS, MOOD_WISH_ICONS, moodLabelKey, wishLabelKey,
} from "../utils/moodKeys";

// Sélecteur de mood pour un Real Vibe — même recette de bottom sheet que le reste de l'app
// (voir WelcomePopup.tsx / date/DatePromptSheet.tsx) : fond blanc, coins arrondis en haut
// uniquement, X discret. INVITE-MOI est mis en avant en première position (léger accent doré,
// seule touche de couleur de tout le sheet) ; les autres moods sont un simple choix monochrome.
// Sélectionner une option ferme le sheet immédiatement (comme ActivityStep du wizard Date) —
// un seul mood actif à la fois, jamais de bouton "valider" séparé.
export default function RealVibeMoodSheet({
  lang, moodKey, wishKey, onChange, onClose,
}: {
  lang: string;
  moodKey: string | null;
  wishKey: string | null;
  onChange: (moodKey: string | null, wishKey: string | null) => void;
  onClose: () => void;
}) {
  const emotionalMoods = MOOD_KEYS.filter(k => k !== "invite_me");
  const InviteIcon = MOOD_ICONS.invite_me;

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{t(lang, "mood_sheet_title")}</div>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        {/* INVITE-MOI — option spéciale, légère touche dorée (seule couleur de tout le sheet) */}
        <button
          onClick={() => onChange(moodKey === "invite_me" ? null : "invite_me", null)}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 12,
            padding: "14px 16px", borderRadius: 16, cursor: "pointer", marginBottom: 10,
            border: moodKey === "invite_me" ? "1.5px solid #b8952e" : "1px solid #eee5d0",
            backgroundColor: "#faf8f3", textAlign: "left",
          }}
        >
          <div style={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <InviteIcon size={18} color="#b8952e" strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#6b5d38", textTransform: "uppercase", letterSpacing: 0.3 }}>{t(lang, "mood_invite_me")}</div>
            <div style={{ fontSize: 12, color: "#8a7a52" }}>{t(lang, "mood_invite_me_sub")}</div>
          </div>
        </button>

        {moodKey === "invite_me" && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, color: "#999", fontWeight: 600, marginBottom: 8 }}>{t(lang, "mood_wish_prompt")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {MOOD_WISH_KEYS.map(w => {
                const WIcon = MOOD_WISH_ICONS[w];
                const selected = wishKey === w;
                return (
                  <button
                    key={w}
                    onClick={() => onChange("invite_me", selected ? null : w)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
                      borderRadius: 20, cursor: "pointer",
                      border: selected ? "1.5px solid #0a0a0a" : "1px solid #eee",
                      backgroundColor: selected ? "#0a0a0a" : "white",
                      color: selected ? "white" : "#333",
                    }}
                  >
                    <WIcon size={14} strokeWidth={1.8} />
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{t(lang, wishLabelKey(w))}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {emotionalMoods.map(k => {
            const Icon = MOOD_ICONS[k];
            const selected = moodKey === k;
            return (
              <button
                key={k}
                onClick={() => onChange(selected ? null : k, null)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  padding: "12px 4px", borderRadius: 14, cursor: "pointer",
                  border: selected ? "1.5px solid #0a0a0a" : "1px solid #eee",
                  backgroundColor: selected ? "#0a0a0a" : "white",
                }}
              >
                <Icon size={18} color={selected ? "white" : "#111"} strokeWidth={1.7} />
                <span style={{ fontSize: 10.5, fontWeight: 600, color: selected ? "white" : "#333", textAlign: "center", lineHeight: 1.2 }}>{t(lang, moodLabelKey(k))}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  ,
    document.body
  );
}
